import { useMemo, useRef } from "react";

import type { Node, Edge } from "@xyflow/react";

import {
  type PipelineStep,
  stateVcsToBuildLabel,
  stateBuildToDeployLabel,
} from "./pipeline-graph-from-state-helpers";
import {
  type PipelineGroupService,
  type PipelineNodeData,
  type PipelineNodeType,
  aggregateStatus,
  mergeWithPhaseStatus,
  pickActiveService,
  getLayoutedElements,
} from "./pipeline-graph-helpers";

export type { PipelineStep } from "./pipeline-graph-from-state-helpers";

export interface UsePipelineGraphFromStateResult {
  nodes: Node[];
  edges: Edge[];
  serviceNames: string[];
}

export function usePipelineGraphFromState(
  pipelineState: PipelineStep[] = [],
  templateServices?: { name: string; type: string }[],
  deployMode?: string,
  vcsConnected?: boolean,
  /** Override the VCS badge label (e.g. "Connected — Provisioning" when stack is provisioning) */
  _vcsStatusLabel?: string,
  /** Whether the org has a VCS connection (even if the app has no repo yet) */
  _vcsOrgConnected?: boolean,
  /** The app's VCS repo URL — null means repo not provisioned yet */
  _vcsRepoUrl?: string | null,
): UsePipelineGraphFromStateResult {
  const prevPositionsRef = useRef<Map<string, { x: number; y: number }>>(
    new Map(),
  );

  return useMemo(() => {
    const nodes: Node<PipelineNodeData>[] = [];
    const edges: Edge[] = [];
    const serviceNames: string[] = [];

    // Group PipelineStep rows by (step, serviceName)
    // Each row is already the latest for its key (materialized view)
    const stepsByService = new Map<string, Map<string, PipelineStep>>();
    for (const step of pipelineState) {
      if (!stepsByService.has(step.step)) {
        stepsByService.set(step.step, new Map<string, PipelineStep>());
      }
      stepsByService.get(step.step)!.set(step.serviceName, step);
    }

    // Get all services that have build or deploy steps
    // Exclude phase-level rows (serviceName === "") — they mask per-service failures
    const buildSteps = stepsByService.get("build") ?? new Map();
    const deploySteps = stepsByService.get("deploy") ?? new Map();
    // Filter out phase-level rows (empty serviceName) from build/deploy
    const buildServiceSteps = new Map(
      [...buildSteps].filter(([name]) => name !== ""),
    );
    const deployServiceSteps = new Map(
      [...deploySteps].filter(([name]) => name !== ""),
    );

    const serviceNamesFromState = new Set<string>();
    for (const [name] of buildServiceSteps)
      if (name) serviceNamesFromState.add(name);
    for (const [name] of deployServiceSteps)
      if (name) serviceNamesFromState.add(name);

    // Combine with template services to get full list
    const templateBuildableServices = (templateServices ?? []).filter(
      (s) => s.type !== "stateful",
    );
    const templateStatefulServices = (templateServices ?? []).filter(
      (s) => s.type === "stateful",
    );

    const allServices = new Set<string>();
    for (const svc of templateBuildableServices) allServices.add(svc.name);
    for (const svc of templateStatefulServices) allServices.add(svc.name);
    for (const name of serviceNamesFromState) allServices.add(name);

    // When template services are available, use them. Otherwise derive from pipelineState.
    const buildableServices =
      templateBuildableServices.length > 0
        ? templateBuildableServices.filter((s) => allServices.has(s.name))
        : Array.from(buildServiceSteps.keys())
            .filter((n) => n !== "")
            .map((n) => ({ name: n, type: "server" }));

    const statefulServices =
      templateStatefulServices.length > 0
        ? templateStatefulServices.filter((s) => allServices.has(s.name))
        : Array.from(deployServiceSteps.keys())
            .filter((n) => n !== "" && !buildServiceSteps.has(n))
            .map((n) => ({ name: n, type: "stateful" }));

    // Fallback: when there are no per-service rows and no template services,
    // but phase-level rows (serviceName === "") DO exist for build/deploy,
    // synthesize a single "app" service so the DAG renders group nodes.
    // This is common for simple stacks that only have phase-level pipeline
    // events (e.g., single-service builds without per-service event rows).
    const hasPhaseRows = buildSteps.has("") || deploySteps.has("");
    if (
      allServices.size === 0 &&
      hasPhaseRows &&
      buildableServices.length === 0 &&
      statefulServices.length === 0
    ) {
      buildableServices.push({ name: "app", type: "server" });
      allServices.add("app");
    }

    // VCS is decoupled from the DAG — shown in the Setup stage sidebar, not as a node.

    // ── Build group node + Deploy group node ──
    if (allServices.size > 0) {
      if (deployMode === "ci_managed") {
        // CI-managed: single CI group node
        const ciServices: PipelineGroupService[] = [
          ...buildableServices,
          ...statefulServices,
        ].map((svc) => {
          const step =
            buildServiceSteps.get(svc.name) ?? deployServiceSteps.get(svc.name);
          serviceNames.push(svc.name);
          return {
            name: svc.name,
            type: svc.type,
            status: step?.status ?? "pending",
            message: step?.message ?? "Waiting...",
          };
        });

        const ciAgg = aggregateStatus(ciServices);
        const activeSvc = pickActiveService(ciServices);
        nodes.push({
          id: "group-ci",
          type: "pipeline-group",
          position: { x: 0, y: 0 },
          data: {
            label: "External CI",
            step: "ci",
            nodeType: "group-ci" as PipelineNodeType,
            status: ciAgg,
            message: "",
            iconName: "Build",
            serviceName: activeSvc,
            isStale: false,
            retryCount: 0,
            deployModeBadge: "External",
            services: ciServices,
          },
        });

        // CI group node is standalone — no incoming edge
      } else {
        const buildBadge =
          deployMode === "ci_build_rusha_deploy" ? "CI" : undefined;
        const deployBadge =
          deployMode === "ci_build_rusha_deploy"
            ? "Rusha"
            : deployMode === "rusha_build_ci_deploy"
              ? "CI"
              : undefined;

        // Build group (buildable services only)
        const buildServices: PipelineGroupService[] = buildableServices.map(
          (svc) => {
            const step = buildServiceSteps.get(svc.name);
            serviceNames.push(svc.name);
            return {
              name: svc.name,
              type: svc.type,
              status: step?.status ?? "pending",
              message: step?.message ?? "Waiting...",
            };
          },
        );

        // Deploy group (buildable + stateful)
        const allDeployServices: PipelineGroupService[] = [
          ...buildableServices,
          ...statefulServices,
        ].map((svc) => {
          const step = deployServiceSteps.get(svc.name);
          if (!serviceNames.includes(svc.name)) serviceNames.push(svc.name);
          return {
            name: svc.name,
            type: svc.type,
            status: step?.status ?? "pending",
            message: step?.message ?? "Waiting...",
          };
        });

        if (buildServices.length > 0) {
          // Merge service-level aggregate with phase-level row (serviceName="").
          // Phase-level failures (e.g., image verification) must override the
          // per-service "completed" aggregate so the group shows the true state.
          const buildPhaseStatus = buildSteps.get("")?.status ?? null;
          const buildAgg = mergeWithPhaseStatus(
            aggregateStatus(buildServices),
            buildPhaseStatus,
          );
          const activeBuildSvc =
            buildAgg === "failed" && buildPhaseStatus === "failed"
              ? (buildServices.find((s) => s.status === "failed")?.name ??
                buildServices[0]?.name)
              : pickActiveService(buildServices);
          const _buildEdgeLabel = stateVcsToBuildLabel(
            buildServiceSteps.get(buildServices[0].name),
            buildServiceSteps,
          );
          nodes.push({
            id: "group-build",
            type: "pipeline-group",
            position: { x: 0, y: 0 },
            data: {
              label: "Build",
              step: "build",
              nodeType: "group-build" as PipelineNodeType,
              status: buildAgg,
              message: "",
              iconName: "Build",
              serviceName: activeBuildSvc,
              isStale: false,
              retryCount: 0,
              deployModeBadge: buildBadge,
              services: buildServices,
            },
          });

          // Build is the first node — no incoming edge
        }

        if (allDeployServices.length > 0) {
          const deployPhaseStatus = deploySteps.get("")?.status ?? null;
          const deployAgg = mergeWithPhaseStatus(
            aggregateStatus(allDeployServices),
            deployPhaseStatus,
          );
          const activeDeploySvc = pickActiveService(allDeployServices);
          const deployEdgeLabel = stateBuildToDeployLabel(
            buildServiceSteps.get(allDeployServices[0].name),
            deployServiceSteps.get(allDeployServices[0].name),
          );
          const deployEdgeSrc = buildServices.length > 0 ? "group-build" : null;
          nodes.push({
            id: "group-deploy",
            type: "pipeline-group",
            position: { x: 0, y: 0 },
            data: {
              label: "Deploy",
              step: "deploy",
              nodeType: "group-deploy" as PipelineNodeType,
              status: deployAgg,
              message: "",
              iconName: "RocketLaunch",
              serviceName: activeDeploySvc,
              isStale: false,
              retryCount: 0,
              deployModeBadge: deployBadge,
              services: allDeployServices,
            },
          });

          if (deployEdgeSrc) {
            edges.push({
              id: "edge-build-group-deploy",
              source: deployEdgeSrc,
              target: "group-deploy",
              type: "default",
              animated: deployAgg === "in_progress",
              label: deployEdgeLabel,
              labelStyle: {
                fill: deployAgg === "failed" ? "#d32f2f" : "#666",
                fontWeight: 600,
                fontSize: 10,
              },
              labelShowBg: false,
            });
          }
        }
      }
    }

    // Apply dagre layout
    const layoutedNodes = getLayoutedElements(nodes, edges);

    // Cache positions for smooth transitions on re-layout
    const newPositions = new Map<string, { x: number; y: number }>();
    for (const node of layoutedNodes) {
      if (node.position) newPositions.set(node.id, node.position);
    }
    prevPositionsRef.current = newPositions;

    return { nodes: layoutedNodes, edges, serviceNames };
  }, [pipelineState, templateServices, deployMode, vcsConnected]);
}
